// src/components/testimonials/TestimonialViewModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, Star, MessageCircle, User, Calendar, 
  Building, Award, ChevronLeft, ChevronRight
} from 'lucide-react';

export const TestimonialViewModal = ({ 
  isOpen, 
  onClose, 
  profileId, 
  profileOwnerName,
  publicTestimonials = []
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const testimonialsPerPage = 6;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const totalPages = Math.ceil(publicTestimonials.length / testimonialsPerPage);
  const startIndex = (currentPage - 1) * testimonialsPerPage;
  const currentTestimonials = publicTestimonials.slice(startIndex, startIndex + testimonialsPerPage);

  const averageRating = publicTestimonials.length > 0 
    ? publicTestimonials.reduce((acc, t) => acc + (t.rating || 5), 0) / publicTestimonials.length 
    : 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform overflow-y-auto">
        <div className="flex flex-col min-h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white sticky top-0 z-10">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Reviews & Reflections
              </h2>
              {publicTestimonials.length > 0 && (
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`h-4 w-4 ${
                          star <= Math.round(averageRating)
                            ? 'text-yellow-400 fill-current' 
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="text-sm text-gray-600 ml-2">
                      {averageRating.toFixed(1)} ({publicTestimonials.length} reviews)
                    </span>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 p-6">
            {publicTestimonials.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No reviews yet
                </h3>
                <p className="text-gray-600">
                  Be the first to share your experience with {profileOwnerName}!
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Testimonials Grid */}
                <div className="grid gap-6">
                  {currentTestimonials.map((testimonial, idx) => (
                    <div 
                      key={idx}
                      className="bg-gray-50 border border-gray-200 rounded-xl p-6 relative"
                    >
                      {/* Quote Mark */}
                      <div className="absolute -top-3 -left-3 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-lg font-bold">"</span>
                      </div>

                      {/* Testimonial Text */}
                      <div className="mb-6">
                        <p className="text-gray-700 text-base leading-relaxed">
                          {testimonial.testimonial}
                        </p>
                      </div>

                      {/* Rating */}
                      <div className="flex items-center gap-1 mb-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star}
                            className={`h-4 w-4 ${
                              star <= (testimonial.rating || 5)
                                ? 'text-yellow-400 fill-current' 
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>

                      {/* Author Info */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
                            {testimonial.clientPhoto ? (
                              <img 
                                src={testimonial.clientPhoto} 
                                alt={testimonial.clientName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {testimonial.clientName}
                            </h4>
                            {(testimonial.clientTitle || testimonial.clientCompany) && (
                              <p className="text-sm text-gray-600 mt-1">
                                {testimonial.clientTitle}
                                {testimonial.clientTitle && testimonial.clientCompany && ' at '}
                                {testimonial.clientCompany}
                              </p>
                            )}
                            {testimonial.projectTitle && (
                              <div className="flex items-center gap-1 mt-2">
                                <Award className="h-3 w-3 text-gray-400" />
                                <span className="text-xs text-gray-500">
                                  Project: {testimonial.projectTitle}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          {testimonial.date ? new Date(testimonial.date).toLocaleDateString() : 'Recently'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </button>

                    <div className="flex items-center gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                            page === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};